import { useEffect, useState } from "react"
import styled from 'styled-components' 
import { closeCreateAncmtModal, createAnnouncement } from 'features/anouncements/announcementSlice';
import { useSelector, useDispatch } from "react-redux"
import CampaignIcon from '@mui/icons-material/Campaign';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { toast } from "react-toastify";
import ModalCloseButton from "components/buttons/ModalCloseButton";

import SpinnerSmall from "components/SpinnerSmall";
import membershipService from "features/memberships/membershipService";
import { useTranslation } from 'react-i18next'

const defaultAudience = {
    visibility: "entire_church",
    systemRoles: [],
    ministryRoleIds: [],
    audienceTagIds: [],
    selectedUserIds: [],
}

const getItemId = (item) => item?.id || item?._id
const getProfileImage = (member) => member?.profileImg?.urlSmall || member?.profileImg?.url || ""
const normalizeArray = (value) => Array.isArray(value) ? value : []
const hasTargetSelectors = (audience) => (
    audience.systemRoles.length > 0 ||
    audience.ministryRoleIds.length > 0 ||
    audience.audienceTagIds.length > 0 ||
    audience.selectedUserIds.length > 0
)

export default function CreateAncmtModal() {
  const { t: translate } = useTranslation()
  const { t } = useTranslation('admin')

    const dispatch = useDispatch()
    const [announcementText, setAnnouncementText] = useState('')
    const [audience, setAudience] = useState(defaultAudience)
    const [systemRoles, setSystemRoles] = useState([])
    const [ministryRoles, setMinistryRoles] = useState([])
    const [audienceTags, setAudienceTags] = useState([])
    const [audienceMembers, setAudienceMembers] = useState([])
    const [selectedMembers, setSelectedMembers] = useState({})
    const [memberSearch, setMemberSearch] = useState("")
    const [debouncedMemberSearch, setDebouncedMemberSearch] = useState("")
    const [audienceLoading, setAudienceLoading] = useState(false)
    const [audienceCount, setAudienceCount] = useState(null)
    const { profileChurch } = useSelector((state) => state.profileChurch)
    const { isAnnouncementCreatedLoading, isAnnouncementCreatedSuccess, isAnnouncementCreatedError, announcementsErrorMessage } = useSelector((state) => state.announcement)
    const { user } = useSelector((state) => state.auth)
    const churchId = profileChurch?._id
    const selectorCount = audience.systemRoles.length + audience.ministryRoleIds.length + audience.audienceTagIds.length + audience.selectedUserIds.length

    const handlePostTextChange = (e) => {
        setAnnouncementText(e.target.value)
    }

    const setAudienceVisibility = (visibility) => {
        if(visibility === "entire_church"){
            setAudience(defaultAudience)
            setSelectedMembers({})
            return
        }

        setAudience((prev) => ({
            ...prev,
            visibility: "targeted",
        }))
    }

    const toggleAudienceValue = (key, value) => {
        setAudience((prev) => {
            const currentValues = normalizeArray(prev[key])
            const exists = currentValues.includes(value)
            return {
                ...prev,
                visibility: "targeted",
                [key]: exists ? currentValues.filter((item) => item !== value) : [...currentValues, value],
            }
        })
    }

    const toggleMember = (member) => {
        const memberId = getItemId(member)
        const isSelected = audience.selectedUserIds.includes(memberId)
        toggleAudienceValue("selectedUserIds", memberId)
        setSelectedMembers((prev) => {
            const next = { ...prev }
            if(isSelected) delete next[memberId]
            else next[memberId] = member
            return next
        })
    }

    const clearTargetAudience = () => {
        setAudience((prev) => ({ ...defaultAudience, visibility: prev.visibility }))
        setSelectedMembers({})
    }

    const handleSubmit = async () => {
        const formData = new FormData();
        const normalizedAudience = audience.visibility === "targeted" ? audience : defaultAudience

        if(announcementText.length < 3 ){
            return toast.error(t('announcements.errors.textRequired'));
        }
        if(!churchId){
            return toast.error(t('announcements.errors.churchNotFound'));
        }
        if(normalizedAudience.visibility === "targeted" && !hasTargetSelectors(normalizedAudience)){
            return toast.error(t('announcements.errors.audienceRequired'));
        }
        if(normalizedAudience.visibility === "targeted" && audienceCount === 0){
            return toast.error(t('announcements.errors.noRecipients'));
        }
        formData.append("announcementText", announcementText);
        formData.append("churchId", churchId);
        formData.append("audience", JSON.stringify(normalizedAudience));
        dispatch(createAnnouncement(formData))
    }

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedMemberSearch(memberSearch.trim()), 300)
        return () => clearTimeout(timeout)
    }, [memberSearch])

    useEffect(() => {
        if(!churchId || !user?.token || audience.visibility !== "targeted") return

        let active = true
        const loadAudienceOptions = async () => {
            try {
                setAudienceLoading(true)
                const [systemRolesData, ministryRolesData, tagsData] = await Promise.all([
                    membershipService.getAudienceSystemRoles(churchId, user.token),
                    membershipService.getMinistryRoles(churchId, user.token),
                    membershipService.getAudienceTags(churchId, user.token),
                ])

                if(!active) return
                setSystemRoles(normalizeArray(systemRolesData.systemRoles))
                setMinistryRoles(normalizeArray(ministryRolesData.ministryRoles))
                setAudienceTags(normalizeArray(tagsData.audienceTags))
            } catch (error) {
                if(active) toast.error(t('announcements.errors.audienceLoad'))
            } finally {
                if(active) setAudienceLoading(false)
            }
        }

        loadAudienceOptions()

        return () => {
            active = false
        }
    }, [audience.visibility, churchId, t, user?.token])

    useEffect(() => {
        if(!churchId || !user?.token || audience.visibility !== "targeted") return

        let active = true
        const loadMembers = async () => {
            try {
                const membersData = await membershipService.getAudienceMembers(churchId, user.token, {
                    search: debouncedMemberSearch,
                    limit: 25,
                })
                if(active) setAudienceMembers(normalizeArray(membersData.members))
            } catch (error) {
                if(active) setAudienceMembers([])
            }
        }
        loadMembers()
        return () => { active = false }
    }, [audience.visibility, churchId, debouncedMemberSearch, user?.token])

    useEffect(() => {
        if(!churchId || !user?.token) return

        let active = true
        const loadAudienceCount = async () => {
            try {
                const normalizedAudience = audience.visibility === "targeted" ? audience : defaultAudience
                if(normalizedAudience.visibility === "targeted" && !hasTargetSelectors(normalizedAudience)){
                    setAudienceCount(0)
                    return
                }

                const countData = await membershipService.resolveAudienceCount(churchId, normalizedAudience, user.token)
                if(active) setAudienceCount(countData.recipientCount)
            } catch (error) {
                if(active) setAudienceCount(null)
            }
        }

        const timeout = setTimeout(loadAudienceCount, 250)

        return () => {
            active = false
            clearTimeout(timeout)
        }
    }, [audience, churchId, user?.token])

    useEffect(() => {
        if(isAnnouncementCreatedSuccess){ 
            toast.success(t('announcements.created'))
            dispatch(closeCreateAncmtModal()) 
        }
    }, [isAnnouncementCreatedSuccess, dispatch, t])

    useEffect(() => {
        if(isAnnouncementCreatedError && announcementsErrorMessage){
            toast.error(announcementsErrorMessage)
        }
    }, [announcementsErrorMessage, isAnnouncementCreatedError])

  return (
    <CreateAncmtModalCont>
        <CAModalContainer>
            <ModalTopHeading>
                <HeadingIcon><CampaignIcon /></HeadingIcon>
                <HeadingCopy>
                    <AnnouncementModalHeading>{translate("web:general.createAnnouncement")}</AnnouncementModalHeading>
                 </HeadingCopy>
            </ModalTopHeading>

            <ModalCloseButton 
                handler={() => dispatch(closeCreateAncmtModal())} 
                size='35px'
                top='10px'
                right='10px'
                />

            <FormSection>
                <FieldTopRow>
                    <FieldLabel>{t('announcements.label')}</FieldLabel>
                    <CharacterCount>{t('announcements.characterCount', { count: announcementText.length })}</CharacterCount>
                </FieldTopRow>
                <TA
                    value={announcementText}
                    onChange={handlePostTextChange}
                    placeholder={translate("web:general.startAnnouncementHere")}
                    />
            </FormSection>

            <AudienceSection>
                <AudienceTopRow>
                    <div>
                        <AudienceTitle>{t('announcements.audience.title')}</AudienceTitle>
                        <AudienceDescription>{t('announcements.audience.description')}</AudienceDescription>
                    </div>
                    {audienceCount !== null && (
                        <AudienceCount><GroupsRoundedIcon /> {t('announcements.audience.recipientCount', { count: audienceCount })}</AudienceCount>
                    )}
                </AudienceTopRow>

                <AudienceModeRow>
                    <AudienceModeButton
                        $selected={audience.visibility === "entire_church"}
                        onClick={() => setAudienceVisibility("entire_church")}
                        type="button"
                    >
                        <GroupsRoundedIcon />
                        <span><strong>{t('announcements.audience.entireChurch')}</strong><small>{t('announcements.audience.entireChurchHint')}</small></span>
                    </AudienceModeButton>
                    <AudienceModeButton
                        $selected={audience.visibility === "targeted"}
                        onClick={() => setAudienceVisibility("targeted")}
                        type="button"
                    >
                        <FilterAltOutlinedIcon />
                        <span><strong>{t('announcements.audience.targeted')}</strong><small>{t('announcements.audience.targetedHint')}</small></span>
                    </AudienceModeButton>
                </AudienceModeRow>

                {audience.visibility === "targeted" && (
                    <TargetOptions>
                        {audienceLoading && <AudienceLoading><SpinnerSmall /></AudienceLoading>}

                        <AudienceLogic>
                            <FilterAltOutlinedIcon />
                            <span>{t('announcements.audience.matchingPrefix')} <strong>{t('announcements.audience.any')}</strong> {t('announcements.audience.matchingSuffix')}</span>
                        </AudienceLogic>

                        <SelectionSummary>
                            <span>{t('announcements.audience.filterCount', { count: selectorCount })}</span>
                            {selectorCount > 0 && <ClearAudienceButton type="button" onClick={clearTargetAudience}><ClearRoundedIcon /> {t('filters.clear')}</ClearAudienceButton>}
                        </SelectionSummary>

                        <AudienceGroup>
                            <AudienceGroupHeader><div><AudienceGroupTitle>{t('announcements.audience.churchRoles')}</AudienceGroupTitle><AudienceGroupHint>{t('announcements.audience.churchRolesHint')}</AudienceGroupHint></div><SelectedCount>{t('announcements.audience.selectedCount', { count: audience.systemRoles.length })}</SelectedCount></AudienceGroupHeader>
                            <ChipRow>
                                {systemRoles.map((role) => (
                                    <AudienceChip
                                        key={role}
                                        $selected={audience.systemRoles.includes(role)}
                                        onClick={() => toggleAudienceValue("systemRoles", role)}
                                        type="button"
                                    >
                                        {t(`roles.${role}`, { defaultValue: role })}
                                    </AudienceChip>
                                ))}
                            </ChipRow>
                        </AudienceGroup>

                        <AudienceGroup>
                            <AudienceGroupHeader><div><AudienceGroupTitle>{t('announcements.audience.ministryRoles')}</AudienceGroupTitle><AudienceGroupHint>{t('announcements.audience.ministryRolesHint')}</AudienceGroupHint></div><SelectedCount>{t('announcements.audience.selectedCount', { count: audience.ministryRoleIds.length })}</SelectedCount></AudienceGroupHeader>
                            <ChipRow>
                                {ministryRoles.length === 0 && <AudienceMuted>{t('announcements.audience.noMinistryRoles')}</AudienceMuted>}
                                {ministryRoles.map((role) => {
                                    const roleId = getItemId(role)
                                    return (
                                        <AudienceChip
                                            key={roleId}
                                            $selected={audience.ministryRoleIds.includes(roleId)}
                                            onClick={() => toggleAudienceValue("ministryRoleIds", roleId)}
                                            type="button"
                                        >
                                            {role.name}
                                        </AudienceChip>
                                    )
                                })}
                            </ChipRow>
                        </AudienceGroup>

                        <AudienceGroup>
                            <AudienceGroupHeader><div><AudienceGroupTitle>{t('announcements.audience.tags')}</AudienceGroupTitle><AudienceGroupHint>{t('announcements.audience.tagsHint')}</AudienceGroupHint></div><SelectedCount>{t('announcements.audience.selectedCount', { count: audience.audienceTagIds.length })}</SelectedCount></AudienceGroupHeader>
                            <ChipRow>
                                {audienceTags.length === 0 && <AudienceMuted>{t('announcements.audience.noTags')}</AudienceMuted>}
                                {audienceTags.map((tag) => {
                                    const tagId = getItemId(tag)
                                    return (
                                        <AudienceChip
                                            key={tagId}
                                            $selected={audience.audienceTagIds.includes(tagId)}
                                            onClick={() => toggleAudienceValue("audienceTagIds", tagId)}
                                            type="button"
                                        >
                                            {tag.name}
                                        </AudienceChip>
                                    )
                                })}
                            </ChipRow>
                        </AudienceGroup>

                        <AudienceGroup>
                            <AudienceGroupHeader><div><AudienceGroupTitle>{t('announcements.audience.members')}</AudienceGroupTitle><AudienceGroupHint>{t('announcements.audience.membersHint')}</AudienceGroupHint></div><SelectedCount>{t('announcements.audience.selectedCount', { count: audience.selectedUserIds.length })}</SelectedCount></AudienceGroupHeader>
                            {Object.values(selectedMembers).length > 0 && (
                                <SelectedMembers>
                                    {Object.values(selectedMembers).map((member) => {
                                        const memberId = getItemId(member)
                                        return <SelectedMember key={memberId}>{member.fullName || member.username || t('roles.member')}<button type="button" onClick={() => toggleMember(member)} aria-label={t('announcements.audience.removeMember', { name: member.fullName || t('roles.member') })}><ClearRoundedIcon /></button></SelectedMember>
                                    })}
                                </SelectedMembers>
                            )}
                            <MemberSearchWrap>
                                <PersonSearchOutlinedIcon />
                                <MemberSearchInput
                                    value={memberSearch}
                                    onChange={(e) => setMemberSearch(e.target.value)}
                                    placeholder={t('announcements.audience.searchMembers')}
                                />
                            </MemberSearchWrap>
                            <MemberPicker>
                                {audienceMembers.length === 0 && <AudienceMuted>{t('announcements.audience.noMembers')}</AudienceMuted>}
                                {audienceMembers.map((member) => {
                                    const memberId = getItemId(member)
                                    return (
                                        <MemberPickButton
                                            key={memberId}
                                            $selected={audience.selectedUserIds.includes(memberId)}
                                            onClick={() => toggleMember(member)}
                                            type="button"
                                        >
                                            <MemberAvatar>
                                                {getProfileImage(member) ? <img src={getProfileImage(member)} alt="" /> : <span>{(member.fullName || member.username || '?').charAt(0).toUpperCase()}</span>}
                                            </MemberAvatar>
                                            <span>{member.fullName || member.username || t('roles.member')}</span>
                                        </MemberPickButton>
                                    )
                                })}
                            </MemberPicker>
                        </AudienceGroup>
                    </TargetOptions>
                )}
            </AudienceSection>

            <ModalFooter>
                <FooterAudience>{audienceCount === null ? t('announcements.audience.countUnavailable') : t('announcements.audience.recipientSelectedCount', { count: audienceCount })}</FooterAudience>
                <PublishButton type="button" disabled={isAnnouncementCreatedLoading || announcementText.trim().length < 3} onClick={handleSubmit}>
                    {isAnnouncementCreatedLoading ? <SpinnerSmall /> : <><CampaignIcon /> {translate("web:general.createAnnouncement")}</>}
                </PublishButton>
            </ModalFooter>
        </CAModalContainer>
    </CreateAncmtModalCont>
  )
}

const AudienceSection = styled.div`
    width: 100%; padding: 16px; margin-bottom: 14px; border: 1px solid #dfe6e8;
    border-radius: 16px; background: linear-gradient(145deg, #ffffff 0%, #f8fafa 100%);
`

const AudienceTopRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 13px;

    @media (max-width: 560px) { align-items: flex-start; flex-direction: column; }
`

const AudienceTitle = styled.div`
    color: #263337; font-size: 14px; font-weight: 750;
`

const AudienceDescription = styled.p`
    margin: 4px 0 0; color: #728084; font-size: 11px; line-height: 1.4;
`

const AudienceCount = styled.div`
    display: flex; align-items: center; gap: 5px; flex-shrink: 0; padding: 7px 9px;
    border-radius: 9px; background: rgba(0, 180, 204, .1); color: var(--themeColor); font-size: 11px; font-weight: 750;
    svg { font-size: 16px; }
`

const AudienceModeRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    @media (max-width: 560px) { grid-template-columns: 1fr; }
`

const AudienceModeButton = styled.button`
    min-width: 0; display: flex; align-items: center; gap: 10px; padding: 12px;
    border: 1px solid ${props => props.$selected ? "var(--themeColor)" : "#dce3e5"};
    border-radius: 12px; background: ${props => props.$selected ? "rgba(0, 180, 204, .09)" : "#fff"};
    color: ${props => props.$selected ? "var(--themeColor)" : "#536065"}; cursor: pointer; text-align: left;
    > svg { flex: 0 0 auto; font-size: 21px; }
    > span { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
    strong { color: ${props => props.$selected ? "var(--themeColor)" : "#344145"}; font-size: 12px; }
    small { color: #7b878b; font-size: 10px; }
    &:hover { border-color: rgba(0, 180, 204, .55); }
`

const TargetOptions = styled.div`
    margin-top: 14px; max-height: 420px; overflow-y: auto; padding-right: 5px;
    scrollbar-width: thin; scrollbar-color: #cbd4d7 transparent;
`

const AudienceLoading = styled.div`
    display: flex;
    justify-content: center;
    padding: 10px 0;
`

const AudienceLogic = styled.div`
    display: flex; align-items: flex-start; gap: 8px; padding: 10px 11px; border-radius: 10px;
    background: #eef7f8; color: #5e6c70; font-size: 11px; line-height: 1.45;
    svg { flex: 0 0 auto; color: var(--themeColor); font-size: 17px; }
`

const SelectionSummary = styled.div`
    display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 10px;
    color: #697579; font-size: 11px; strong { color: #344145; }
`

const ClearAudienceButton = styled.button`
    display: flex; align-items: center; gap: 4px; padding: 5px 7px; border: 0; border-radius: 7px;
    background: transparent; color: #9c4238; cursor: pointer; font-size: 10px; font-weight: 700;
    svg { font-size: 14px; } &:hover { background: #fff0ee; }
`

const AudienceGroup = styled.div`
    margin-top: 11px; padding: 13px; border: 1px solid #e3e8ea; border-radius: 12px; background: #fff;
`

const AudienceGroupHeader = styled.div`
    display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 9px;
`

const AudienceGroupTitle = styled.div`
    color: #3d4a4e; font-size: 12px; font-weight: 750;
`

const AudienceGroupHint = styled.div`
    margin-top: 2px; color: #899397; font-size: 10px;
`

const SelectedCount = styled.span`
    flex-shrink: 0; color: #7a868a; font-size: 10px;
`

const ChipRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`

const AudienceChip = styled.button`
    border: 1px solid ${props => props.$selected ? "var(--themeColor)" : "#d7dfe2"};
    background: ${props => props.$selected ? "var(--themeColor)" : "#f7f9f9"};
    color: ${props => props.$selected ? "#fff" : "#455357"}; border-radius: 9px;
    padding: 7px 10px; font-size: 11px; font-weight: 650; cursor: pointer;
    &:hover { border-color: var(--themeColor); }
`

const AudienceMuted = styled.div`
    color: #98a2a6; font-size: 11px; line-height: 1.4;
`

const MemberSearchWrap = styled.div`
    height: 40px; display: flex; align-items: center; gap: 8px; margin-bottom: 9px; padding: 0 11px;
    border: 1px solid #d7dfe2; border-radius: 10px; background: #fff; color: #849095;
    &:focus-within { border-color: var(--themeColor); box-shadow: 0 0 0 3px rgba(0, 180, 204, .1); }
    > svg { font-size: 18px; }
`

const MemberSearchInput = styled.input`
    width: 100%; min-width: 0; border: 0; outline: 0; background: transparent;
    color: #344145; font-size: 12px; &::placeholder { color: #9aa4a8; }
`

const MemberPicker = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    @media (max-width: 568px) {
        grid-template-columns: 1fr;
    }
`

const MemberPickButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    border: 1px solid ${props => props.$selected ? "var(--themeColor)" : "#e2e8ea"};
    background: ${props => props.$selected ? "rgba(0, 180, 204, .09)" : "#fff"};
    border-radius: 10px;
    padding: 8px;
    cursor: pointer;
    color: #3f4c50; font-size: 11px;
    text-align: left;

    > span:last-child{
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`

const MemberAvatar = styled.span`
    width: 26px; height: 26px; flex: 0 0 26px; display: grid; place-items: center; overflow: hidden;
    border-radius: 50%; background: rgba(0, 180, 204, .13); color: var(--themeColor); font-size: 10px; font-weight: 800;
    img { width: 100%; height: 100%; object-fit: cover; }
`

const SelectedMembers = styled.div`
    display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 9px;
`

const SelectedMember = styled.span`
    display: inline-flex; align-items: center; gap: 4px; padding: 5px 5px 5px 8px; border-radius: 8px;
    background: rgba(0, 180, 204, .1); color: #405055; font-size: 10px; font-weight: 650;
    button { width: 20px; height: 20px; display: grid; place-items: center; padding: 0; border: 0; border-radius: 6px; background: transparent; color: #718085; cursor: pointer; }
    button:hover { background: rgba(0, 180, 204, .16); color: var(--themeColor); }
    svg { font-size: 13px; }
`

const CreateAncmtModalCont = styled.div`
    position: fixed;
    z-index: 11;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
    background: rgba(13, 25, 29, .56);
    backdrop-filter: blur(2px);
`

const CAModalContainer = styled.div`
    width: min(760px, 100%); max-height: 92vh; padding: 22px; background: #fff;
    border: 1px solid #dfe5e7; border-radius: 20px;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: auto;
    box-shadow: 0 24px 70px rgba(13, 29, 34, .24);
    scrollbar-width: thin;
    scrollbar-color: #cbd4d7 transparent;
    @media (max-width: 768px) {
        padding: 18px; border-radius: 16px;
    }
`

const TA = styled.textarea`
    width: 100%; min-height: 150px; resize: vertical; padding: 13px 14px;
    border: 1px solid #d7dfe2; border-radius: 12px; outline: none; background: #f9fbfb;
    color: #344145; font: inherit; font-size: 13px; line-height: 1.55;
    transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
    &::placeholder { color: #98a3a7; }
    &:focus { border-color: var(--themeColor); background: #fff; box-shadow: 0 0 0 3px rgba(0, 180, 204, .1); }
`

const FormSection = styled.section`
    width: 100%; margin-bottom: 14px;
`

const FieldTopRow = styled.div`
    display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 7px;
`

const FieldLabel = styled.label`
    color: #39464a; font-size: 12px; font-weight: 750;
`

const CharacterCount = styled.span`
    color: #8b969a; font-size: 10px;
`

const ModalFooter = styled.footer`
    position: sticky; bottom: -22px; display: flex; align-items: center; justify-content: space-between; gap: 12px;
    margin: 2px -22px -22px; padding: 14px 22px; border-top: 1px solid #e3e8ea; background: rgba(255, 255, 255, .96);
    backdrop-filter: blur(8px);
    @media (max-width: 560px) { align-items: stretch; flex-direction: column; bottom: -18px; margin: 2px -18px -18px; padding: 13px 18px; }
`

const FooterAudience = styled.span`
    color: #778387; font-size: 10px;
`

const PublishButton = styled.button`
    min-width: 190px; min-height: 42px; display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 0 16px; border: 0; border-radius: 11px; background: var(--themeColor); color: #fff;
    cursor: pointer; font-size: 12px; font-weight: 750;
    &:hover { filter: brightness(.95); }
    &:disabled { background: #cbd4d7; color: #7d898d; cursor: default; filter: none; }
    svg { font-size: 18px; }
`

const ModalTopHeading = styled.div`
    width: 100%; display: flex; align-items: flex-start; gap: 11px; padding-right: 38px; margin-bottom: 18px;
`

const HeadingIcon = styled.span`
    width: 42px; height: 42px; flex: 0 0 42px; display: grid; place-items: center;
    border-radius: 12px; background: var(--themeColor); color: #fff; svg { font-size: 23px; }
`

const HeadingCopy = styled.div`
    min-width: 0; padding-top: 2px;
    p { margin: 5px 0 0; color: #758185; font-size: 11px; line-height: 1.4; }
`
 

const AnnouncementModalHeading = styled.div`
  color: #283539; font-size: 18px; font-weight: 780; line-height: 1.2;
`
